import React from 'react';
import {
  TemplateType,
  type Agency,
  type Caregiver,
  type FilledTemplate,
  type Patient,
  type PDFFieldInfo,
  type Template,
} from '../../../types';
import type { DemoRoute } from '../demoMode';
import type { FeatureTour } from '../tourTypes';

const TemplatesPage = React.lazy(() => import('../../templates/TemplatesPage'));

// Invented demo data only — no real person or agency.
const daysAgo = (d: number) => new Date(Date.now() - d * 86400_000).toISOString();
const DEMO_AGENCY = { id: 'demo', name: 'Demo Pflege' } as Agency;

const field = (name: string): PDFFieldInfo => ({ name, type: 'text' });

const tpl = (
  id: string,
  name: string,
  description: string,
  type: TemplateType,
  fields: string[],
  days: number,
): Template => ({
  id,
  name,
  description,
  filename: `${name}.pdf`,
  filepath: `demo/${id}.pdf`,
  filesize: 84_000 + fields.length * 3_100,
  mimetype: 'application/pdf',
  type,
  fields: fields.map(field),
  agencyId: 'demo',
  agency: DEMO_AGENCY,
  isActive: true,
  hasStatus: false,
  createdAt: daysAgo(days),
  updatedAt: daysAgo(days),
});

const TEMPLATES: Template[] = [
  tpl('t1', 'Betreuungsvertrag', 'Vertrag zwischen Klient, Betreuerin und Agentur', TemplateType.GENERAL,
    ['client_full_name', 'client_address', 'caregiver_full_name', 'case_start_date', 'monthly_fee',
      'client_online_signature', 'caregiver_online_signature', 'agency_online_signature'], 120),
  tpl('t2', 'Vollmacht', 'Vollmacht für Angehörige', TemplateType.PATIENT,
    ['client_full_name', 'client_birth_date', 'representative_name', 'client_online_signature'], 95),
  tpl('t3', 'Datenschutzerklärung', 'Einwilligung nach DSGVO', TemplateType.PATIENT,
    ['client_full_name', 'client_address', 'today', 'client_online_signature'], 80),
  tpl('t4', 'Übergabeprotokoll', 'Übergabe beim Betreuungswechsel', TemplateType.CAREGIVER,
    ['caregiver_full_name', 'client_full_name', 'next_turnus_start_date', 'caregiver_online_signature'], 60),
  tpl('t5', 'Meldezettel-Vorlage', 'Anmeldung der Betreuerin', TemplateType.CAREGIVER,
    ['caregiver_full_name', 'caregiver_birth_date', 'caregiver_nationality', 'client_address'], 40),
];

const PATIENTS = [
  { id: 'p1', firstName: 'Josefine', lastName: 'Huber', email: 'maria.berger@example.at', dateOfBirth: '1936-04-12', isActive: true },
  { id: 'p2', firstName: 'Karl', lastName: 'Wagner', email: 'karl.wagner@example.at', dateOfBirth: '1941-09-03', isActive: true },
  { id: 'p3', firstName: 'Hermine', lastName: 'Steiner', email: 'steiner.familie@example.at', dateOfBirth: '1939-01-27', isActive: true },
] as unknown as Patient[];

const CAREGIVERS = [
  { id: 'c1', firstName: 'Ana', lastName: 'Popescu', email: 'ana.popescu@example.ro', isActive: true },
  { id: 'c2', firstName: 'Zuzana', lastName: 'Kováčová', email: 'zuzana.k@example.sk', isActive: true },
] as unknown as Caregiver[];

const filled = (
  id: string,
  template: Template,
  days: number,
  sig: Partial<FilledTemplate>,
  extra: Partial<FilledTemplate> = {},
): FilledTemplate => ({
  id,
  filename: `${id}.pdf`,
  filepath: `demo/filled/${id}.pdf`,
  originalFilename: `${template.name}_Josefine_Huber.pdf`,
  filesize: template.filesize + 12_000,
  patientId: 'p1',
  patientName: 'Josefine Huber',
  caregiverId: 'c1',
  caregiverName: 'Ana Popescu',
  caseNumber: 1042,
  templateId: template.id,
  template,
  agencyId: 'demo',
  agency: DEMO_AGENCY,
  createdBy: 'demo',
  status: null,
  createdAt: daysAgo(days),
  updatedAt: daysAgo(days),
  hasClientSignatureField: false,
  hasCaregiverSignatureField: false,
  hasAgencySignatureField: false,
  clientSignatureCompleted: false,
  caregiverSignatureCompleted: false,
  agencySignatureCompleted: false,
  signatureLinksExpiresAt: daysAgo(-14),
  ...sig,
  ...extra,
});

// The Betreuungsvertrag is the newest row, so it is the first match for the
// tour's spotlights: client signed, caregiver and agency still pending.
const FILLED: FilledTemplate[] = [
  filled('f1', TEMPLATES[0], 0.1, {
    hasClientSignatureField: true,
    hasCaregiverSignatureField: true,
    hasAgencySignatureField: true,
    clientSignatureLink: 'https://example.invalid/sign/demo-client',
    caregiverSignatureLink: 'https://example.invalid/sign/demo-caregiver',
    clientSignatureCompleted: true,
    clientSignatureTimestamp: daysAgo(0.05),
  }, { sentAt: daysAgo(0.09), sentBy: 'office@demo-pflege.at', sentTo: 'maria.berger@example.at' }),
  filled('f2', TEMPLATES[1], 1, {
    hasClientSignatureField: true,
    clientSignatureLink: 'https://example.invalid/sign/demo-client-2',
  }, { caregiverId: undefined, caregiverName: undefined }),
  filled('f3', TEMPLATES[2], 3, {
    hasClientSignatureField: true,
    clientSignatureLink: 'https://example.invalid/sign/demo-client-3',
    clientSignatureCompleted: true,
    clientSignatureTimestamp: daysAgo(2.5),
  }, { caregiverId: undefined, caregiverName: undefined, sentAt: daysAgo(2.4), sentBy: 'auto:signature', sentTo: 'maria.berger@example.at' }),
  filled('f4', TEMPLATES[3], 6, {
    hasCaregiverSignatureField: true,
    caregiverSignatureLink: 'https://example.invalid/sign/demo-caregiver-4',
    caregiverSignatureCompleted: true,
    caregiverSignatureTimestamp: daysAgo(5.8),
  }),
];

const paginated = <T,>(data: T[]) => ({ data, total: data.length, page: 1, limit: 100, totalPages: 1 });
const ok = (data: unknown) => ({ success: true, data });

const filledList = (params: Record<string, string>) => {
  let rows = FILLED;
  if (params.templateId) rows = rows.filter((r) => r.templateId === params.templateId);
  if (params.search) {
    const q = String(params.search).toLowerCase();
    rows = rows.filter((r) => `${r.originalFilename} ${r.patientName} ${r.caregiverName}`.toLowerCase().includes(q));
  }
  return paginated(rows);
};

const mocks: DemoRoute[] = [
  { path: /^\/templates$/, reply: () => TEMPLATES },
  { path: /^\/templates\/filled$/, reply: ({ params }) => filledList(params) },
  { path: /^\/templates\/filled\/([^/]+)$/, reply: ({ match }) => FILLED.find((f) => f.id === match[1]) || FILLED[0] },
  { path: /^\/templates\/([^/]+)$/, reply: ({ match }) => TEMPLATES.find((tp) => tp.id === match[1]) || TEMPLATES[0] },
  // Loaded by the always-mounted batch-fill dialog, the fill dialog and the
  // filled list — never let the agency's real clients/caregivers show up.
  { path: /^\/patients$/, reply: () => paginated(PATIENTS) },
  { path: /^\/patients\/([^/]+)$/, reply: ({ match }) => ok(PATIENTS.find((p) => p.id === match[1]) || PATIENTS[0]) },
  { path: /^\/caregivers$/, reply: () => paginated(CAREGIVERS) },
  { path: /^\/caregivers\/([^/]+)$/, reply: ({ match }) => ok(CAREGIVERS.find((c) => c.id === match[1]) || CAREGIVERS[0]) },
  { path: /^\/manual-shifts\/by-patient\/([^/]+)$/, reply: () => [] },
  // "E-Mail-Empfänger" tab and the send dialog list the agency's saved
  // recipient lists — real addresses, so answer with an invented one.
  {
    path: /^\/templates\/lists\/email$/,
    reply: () => [{
      id: 'el1', name: 'Familie Huber', emails: ['maria.berger@example.at', 'josef.huber@example.at'],
      description: 'Angehörige von Frau Huber', agencyId: 'demo', createdAt: daysAgo(30), updatedAt: daysAgo(30),
    }],
  },
  { path: /^\/templates\/email\/status$/, reply: () => ({ configured: true, connected: true, provider: 'SMTP' }) },
  { path: /^\/templates\/filled\/[^/]+\/auto-attachments$/, reply: () => ({ attachments: [], missing: [], emailTemplate: null }) },
  // Writes whose response the dialogs read (the generic `data: null` would
  // leave the fill dialog without a filled document / the batch dialog empty).
  {
    method: 'post',
    path: /^\/templates\/fill$/,
    delayMs: 800,
    reply: () => ({ success: true, filledPdfPath: FILLED[0].filepath, filledTemplateId: FILLED[0].id, message: 'OK' }),
  },
  {
    method: 'post',
    path: /^\/templates\/fill-batch$/,
    delayMs: 1200,
    reply: ({ body }) => {
      const templateIds: string[] = body?.templateIds || [];
      const recipients: Array<{ patientId?: string; caregiverId?: string }> = body?.recipients || [];
      return {
        results: templateIds.flatMap((templateId) => recipients.map((r, i) => {
          const template = TEMPLATES.find((tp) => tp.id === templateId) || TEMPLATES[0];
          const patient = PATIENTS.find((p) => p.id === r.patientId);
          const caregiver = CAREGIVERS.find((c) => c.id === r.caregiverId);
          return {
            templateId, templateName: template.name,
            patientId: r.patientId, patientName: patient ? `${patient.firstName} ${patient.lastName}` : undefined,
            caregiverId: r.caregiverId, caregiverName: caregiver ? `${caregiver.firstName} ${caregiver.lastName}` : undefined,
            success: true, filledTemplateId: `demo-batch-${templateId}-${i}`, outputFilename: `${template.name}.pdf`,
          };
        })),
      };
    },
  },
];

const k = (key: string) => `featurePreview.templates.${key}`;

export const templatesTour: FeatureTour = {
  Page: TemplatesPage,
  mocks,
  chapters: [
    {
      title: k('tourLibraryTitle'),
      steps: [
        { at: 0.5, target: 'tp-library', balloon: { title: k('hs1Title'), text: k('hs1Text') } },
        { at: 4.5, target: 'tp-help', balloon: { title: k('tourHelpTitle'), text: k('tourHelpText') } },
        { at: 7, target: 'tp-help', action: 'click' },
        { at: 8, target: 'tp-placeholder-search', balloon: { title: k('tourPlaceholdersTitle'), text: k('tourPlaceholdersText') } },
      ],
      duration: 13,
    },
    {
      title: k('ch2'),
      steps: [
        { at: 0.5, target: 'tp-fill', balloon: { title: k('tourFillTitle'), text: k('tourFillText') } },
        { at: 3, target: 'tp-fill', action: 'click' },
        { at: 3.8, target: 'tp-fill-client', balloon: { title: k('hs2Title'), text: k('hs2Text') } },
        { at: 7.5, target: 'tp-fill-caregiver', balloon: { title: k('tourCaregiverTitle'), text: k('tourCaregiverText') } },
        { at: 10.5, target: 'tp-fill-submit', balloon: { title: k('tourSubmitTitle'), text: k('tourSubmitText') } },
      ],
      duration: 14,
    },
    {
      title: k('ch3'),
      steps: [
        { at: 0.3, target: 'tp-filled-tab', action: 'click' },
        { at: 1.2, target: 'tp-filled-person', balloon: { title: k('tourFilledTitle'), text: k('tourFilledText') } },
        { at: 5, target: 'tp-signature-status', balloon: { title: k('hs3Title'), text: k('hs3Text') } },
        { at: 10, target: 'tp-signature-status', balloon: { title: k('tourStatusTitle'), text: k('tourStatusText') } },
      ],
      duration: 14,
    },
  ],
  savings: {
    aLabel: k('savA'), aDefault: 30, aMax: 300,
    bLabel: k('savB'), b: 15,
    toHoursPerMonth: (a, b) => (a * b) / 60,
  },
};
